/**
 * UseCaseIllustrations.jsx
 * Decorative mock-UI renderings for the landing page use-case tiles.
 * Pure JSX/SVG, monochrome to match the editorial palette, no real data.
 */

import React from 'react';

const CREAM = '#FFFFFF';
const CARD = '#161616';
const HAIRLINE = 'rgba(255, 255, 255, 0.10)';
const HAIRLINE_STRONG = 'rgba(255, 255, 255, 0.22)';
const MUTED = 'rgba(255, 255, 255, 0.70)';
const FAINT = 'rgba(255, 255, 255, 0.45)';
const GHOST = 'rgba(255, 255, 255, 0.25)';

/** A window chrome anchored to the tile's bottom edge so it reads as a peek. */
const Frame = ({ title, children }) => (
  <div
    aria-hidden
    className="absolute left-5 right-5 top-5 -bottom-px rounded-t-md overflow-hidden flex flex-col font-sans-body"
    style={{
      background: CARD,
      border: `1px solid ${HAIRLINE_STRONG}`,
      borderBottom: 'none',
      boxShadow: '0 24px 60px -24px rgba(0,0,0,0.9)',
    }}
  >
    <div
      className="flex items-center gap-2 px-3 h-7 flex-shrink-0"
      style={{ borderBottom: `1px solid ${HAIRLINE}` }}
    >
      <span className="flex gap-1">
        {[0, 1, 2].map((i) => (
          <span key={i} className="block w-1.5 h-1.5 rounded-full" style={{ background: GHOST }} />
        ))}
      </span>
      <span className="text-[10px] tracking-wide" style={{ color: FAINT }}>
        {title}
      </span>
    </div>
    <div className="p-3 flex-1 flex flex-col text-[11px]" style={{ color: MUTED }}>
      {children}
    </div>
  </div>
);

const REPORT_ROWS = [
  ['US Treasury 4.25% 2031', '1,000', '$992,340', '31.2%'],
  ['Apple Inc', '2,400', '$555,360', '17.5%'],
  ['Vanguard Total Bond', '9,150', '$681,240', '21.4%'],
  ['Microsoft Corp', '1,120', '$472,864', '14.9%'],
];

export const ReportIllustration = () => (
  <Frame title="Portfolio Appraisal">
    <div className="flex items-baseline justify-between mb-2">
      <span className="font-display text-[13px]" style={{ color: CREAM }}>
        Total portfolio value
      </span>
      <span className="text-[10px]" style={{ color: FAINT }}>
        As of Sep 10, 2026
      </span>
    </div>
    <div className="flex items-baseline gap-2 mb-2">
      <span className="text-[18px] font-medium tabular-nums" style={{ color: CREAM }}>
        $3,178,420
      </span>
      <span className="text-[10px] tabular-nums" style={{ color: FAINT }}>
        +4.8% YTD
      </span>
    </div>
    <svg viewBox="0 0 300 44" className="w-full h-11 mb-3" preserveAspectRatio="none">
      <defs>
        <linearGradient id="uc-area" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={CREAM} stopOpacity="0.16" />
          <stop offset="100%" stopColor={CREAM} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path
        d="M0 34 L30 30 L60 32 L90 24 L120 26 L150 18 L180 21 L210 14 L240 16 L270 9 L300 6 L300 44 L0 44 Z"
        fill="url(#uc-area)"
      />
      <path
        d="M0 34 L30 30 L60 32 L90 24 L120 26 L150 18 L180 21 L210 14 L240 16 L270 9 L300 6"
        fill="none"
        stroke={CREAM}
        strokeOpacity="0.8"
        strokeWidth="1.25"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
    <div
      className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 text-[9px] uppercase tracking-[0.12em] pb-1"
      style={{ color: FAINT, borderBottom: `1px solid ${HAIRLINE}` }}
    >
      <span>Security</span>
      <span>Qty</span>
      <span>Value</span>
      <span>Weight</span>
    </div>
    {REPORT_ROWS.map(([name, qty, value, weight]) => (
      <div
        key={name}
        className="grid grid-cols-[1fr_auto_auto_auto] gap-x-3 py-1.5 tabular-nums"
        style={{ borderBottom: `1px solid ${HAIRLINE}` }}
      >
        <span className="truncate" style={{ color: CREAM }}>
          {name}
        </span>
        <span>{qty}</span>
        <span>{value}</span>
        <span>{weight}</span>
      </div>
    ))}
  </Frame>
);

export const ChatIllustration = () => (
  <Frame title="Analyst">
    <div className="flex justify-end mb-3">
      <span
        className="max-w-[78%] rounded-2xl rounded-br-sm px-3 py-2 leading-snug"
        style={{ background: 'rgba(255,255,255,0.10)', color: CREAM }}
      >
        How did fixed income perform this quarter?
      </span>
    </div>
    <div className="max-w-[88%] leading-snug mb-2">
      <p style={{ color: CREAM }}>
        Fixed income returned <span className="tabular-nums">+1.84%</span> for Q3, led by
        intermediate Treasuries. Portfolio duration ended the quarter at{' '}
        <span className="tabular-nums">5.2 years</span>.
      </p>
    </div>
    <div className="flex items-center gap-1.5 mb-auto">
      <span className="text-[9px] uppercase tracking-[0.12em]" style={{ color: FAINT }}>
        Sources
      </span>
      {['Performance', 'Holdings'].map((s) => (
        <span
          key={s}
          className="text-[9px] px-1.5 py-0.5 rounded-full"
          style={{ border: `1px solid ${HAIRLINE_STRONG}`, color: MUTED }}
        >
          {s}
        </span>
      ))}
    </div>
    <div
      className="mt-3 flex items-center justify-between rounded-full px-3 py-2"
      style={{ border: `1px solid ${HAIRLINE_STRONG}` }}
    >
      <span style={{ color: FAINT }}>Ask about your portfolio…</span>
      <span
        className="w-5 h-5 rounded-full flex items-center justify-center text-[11px] leading-none"
        style={{ background: CREAM, color: CARD }}
      >
        ↑
      </span>
    </div>
  </Frame>
);
